import { useState, useContext, useEffect } from 'react';
import SleepTable from './components/SleepTable';
import { RELATIVE_PAGE_PATHS, TRACKER_SUBTYPES } from '../../utils/constants';
import { ITrackerRecord } from '../../interfaces/ITrackerRecord.interface';
import {
  ISleepTable,
  ISleepTableData,
  ITableTimeRemainder,
} from './helper/ISleepAnalytics.interface';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { IUser } from '../../interfaces/IUser.interface';
import SleepCalendar from './components/SleepCalendar';
import BirthInfo from './components/BirthInfo';
import TrackerSeparator from './components/TrackerSeparator';
import SleepFooter from './components/SleepFooter';
import {
  formatNumber,
  getSleepTableRowDataForDay,
  separateRecordsForPeriod,
} from './helper/sleepUtils';
import DatePickerCalendar from './components/DatePickerCalendar';
import { DatesContext } from './components/DateProvider';
import TableEmptyState from './components/TableEmptyState';

interface ISleepAnalytics {
  trackerRecords: ITrackerRecord[];
  currentUser: IUser;
  disableTrackerLinks?: boolean;
}

const SleepAnalytics: React.FC<ISleepAnalytics> = ({
  trackerRecords,
  currentUser,
  disableTrackerLinks = false,
}) => {
  const [sleepTableData, setSleepTableData] = useState<ISleepTable>();
  const [separatedDays, setSeparatedDays] = useState<ITrackerRecord[][]>([[]]);
  const { startDate, endDate } = useContext(DatesContext);

  const { t } = useTranslation();

  const handleSearch = (startDate: Date, endDate: Date) => {
    const oneDayBeforeStartDate = new Date(startDate.getTime());
    oneDayBeforeStartDate.setDate(startDate.getDate() - 1);

    const sleepRecords = trackerRecords
      .filter(
        (record) =>
          record.subType === TRACKER_SUBTYPES.SLEEP &&
          new Date(record.date) >= new Date(currentUser?.childDateOfBirth)
      )
      //@ts-ignore
      .sort((a, b) => new Date(a.date) - new Date(b.date));
    if (sleepRecords.length > 0) {
      const separatedDays: ITrackerRecord[][] = separateRecordsForPeriod(
        oneDayBeforeStartDate,
        endDate,
        sleepRecords
      );

      setSeparatedDays(separatedDays);

      const tableData: ISleepTableData[] = [];
      let accumulatedMinutesForNextDay: ITableTimeRemainder = {
        action: '',
        remainder: 0,
      };
      separatedDays.forEach((currentDayRecords: ITrackerRecord[], i) => {
        const tableDataForDay = getSleepTableRowDataForDay(
          currentDayRecords,
          separatedDays[i - 1],
          accumulatedMinutesForNextDay,
          currentUser?.childDateOfBirth
        );
        tableData.push(tableDataForDay.currentRowData);
        accumulatedMinutesForNextDay = tableDataForDay.timeRemainder;
      });

      const periodLengthDays =
        moment(endDate).diff(moment(startDate), 'days') + 1;

      if (separatedDays.length > 0 && separatedDays[0].length > 0) {
        if (new Date(separatedDays[0][0].date) < startDate) {
          // Delete the first row from the table if we have records for previous date.
          tableData.shift();
        }
      }

      const averageTableData: ISleepTableData = tableData.reduce(
        (acc, tableRecord) => {
          return {
            dateAndAge: acc.dateAndAge,
            nighttimeSleep:
              acc.nighttimeSleep +
              tableRecord.nighttimeSleep / periodLengthDays,
            daytimeSleep:
              acc.daytimeSleep + tableRecord.daytimeSleep / periodLengthDays,
            totalSleep:
              acc.totalSleep + tableRecord.totalSleep / periodLengthDays,
            totalAwakening: formatNumber(
              acc.totalAwakening + tableRecord.totalAwakening / periodLengthDays
            ),
            daytimeNaps: formatNumber(
              acc.daytimeNaps + tableRecord.daytimeNaps / periodLengthDays
            ),
            nightWaking: formatNumber(
              acc.nightWaking + tableRecord.nightWaking / periodLengthDays
            ),
            lull: formatNumber(acc.lull + tableRecord.lull / periodLengthDays),
          };
        },
        {
          dateAndAge: t('analytics:sleepAnalytics:periodAverage'),
          nighttimeSleep: 0,
          daytimeSleep: 0,
          totalSleep: 0,
          totalAwakening: 0,
          daytimeNaps: 0,
          nightWaking: 0,
          lull: 0,
        }
      );

      setSleepTableData({
        tableData: tableData,
        averageData: averageTableData,
      });
    }
  };

  useEffect(() => {
    if (endDate) {
      handleSearch(startDate, endDate);
    }
  }, [trackerRecords, endDate]);

  return (
    <div>
      <h5 className="primaryHeader">
        <p>{t('analytics:sleepAnalytics:title-sleep')} </p>
        <span>
          {t('analytics:shared.to')} {currentUser?.childName?.toUpperCase()}
        </span>
      </h5>
      <BirthInfo currentUser={currentUser} />
      <div id="sleep">
        <DatePickerCalendar />
        <div id="tracker-separator-sleep">
          <TrackerSeparator
            trackerLink={disableTrackerLinks ? undefined : RELATIVE_PAGE_PATHS.SLEEP}
            trackerIcon={'sleep.svg'}
            trackerType={TRACKER_SUBTYPES.SLEEP}
          />
        </div>
        {sleepTableData && sleepTableData.tableData.length > 0 ? (
          <>
            <div id="sleep-section">
              <div>
                <SleepCalendar
                  period={{ startDate, endDate }}
                  data={separatedDays}
                />
              </div>
            </div>
            <div id="sleep-table">
              <SleepTable
                data={sleepTableData.tableData}
                footer={sleepTableData.averageData}
              />
            </div>
          </>
        ) : (
          <TableEmptyState />
        )}
        <SleepFooter />
      </div>
    </div>
  );
};

export default SleepAnalytics;

